import React, { useState, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import TemplatesIcon from '../icons/templates';
import APIIcon from '../icons/api';
import IntegrationsIcon from '../icons/integrations';
import RecipientsIcon from '../icons/verified';
import TeamIcon from '../icons/users';
import CogIcon from '../icons/cog';
import Avatar from './avatar';
import Images from '../icons/images';
import PreviewsIcon from '../icons/previews';
import { useAuth } from '../auth';
import BrandSettingIcon from '../icons/brand-setting';
import { NavItem } from './nav-item';
import ThemeLogo from './theme-logo';
import { cn } from './cn';
import { sidebarGlassClasses } from './recipes/nav';
import { AccountAnalyticsSync } from '../account-analytics-sync';
import NavPlanUpgrade from '../components/nav-plan-upgrade';

const MenuItems = () => {
  const { asPath } = useRouter();

  return (
    <>
      <div className="px-1.5 mb-2 mt-1">
        <span className="text-[11px] font-medium text-neutral-400 dark:text-white/40 uppercase tracking-wider">
          Design
        </span>
      </div>
      <NavItem href="/templates" icon={<TemplatesIcon />} isActive={asPath.startsWith('/templates')}>
        Templates
      </NavItem>
      <NavItem href="/images" icon={<Images />} isActive={asPath.startsWith('/images')}>
        Images
      </NavItem>
      <NavItem href="/brand" icon={<BrandSettingIcon />} isActive={asPath.startsWith('/brand')}>
        Brand
      </NavItem>
      <NavItem
        href="/previews"
        icon={<PreviewsIcon />}
        isActive={asPath.startsWith('/previews') || asPath.startsWith('/preview/')}>
        Live Previews
      </NavItem>
      <div className="px-1.5 mb-2 !mt-6">
        <span className="text-[11px] font-medium text-neutral-400 dark:text-white/40 uppercase tracking-wider">
          Settings
        </span>
      </div>
      <NavItem href="/recipients" icon={<RecipientsIcon />} isActive={asPath.startsWith('/recipients')}>
        Test Recipients
      </NavItem>
      <NavItem href="/team" icon={<TeamIcon />} isActive={asPath.startsWith('/team')}>
        Invite Team Members
      </NavItem>
      <NavItem
        href="/integrations"
        icon={<IntegrationsIcon />}
        isActive={asPath.startsWith('/integrations')}
        announcementId="integrations"
      >
        Integrations
      </NavItem>
      <NavItem href="/api-keys" icon={<APIIcon />} isActive={asPath.startsWith('/api-keys')}>
        API
      </NavItem>
      <NavItem href="/account" icon={<CogIcon />} isActive={asPath.startsWith('/account')}>
        Account Settings
      </NavItem>
    </>
  );
};

function Sidebar({ isOpen, toggleSidebar }) {
  const router = useRouter();
  useEffect(() => {
    const handleRouteChange = () => {
      toggleSidebar(false);
    };

    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, []);

  return (
    <>
      <div className="sidebar sidebar-mobile md:hidden">
        <Transition show={isOpen} className="fixed inset-0 z-40 flex">
          <Transition.Child
            onClick={() => toggleSidebar(false)}
            className="fixed inset-0"
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute inset-0 bg-neutral-900/40 backdrop-blur-sm" />
          </Transition.Child>
          <Transition.Child
            enter="transition ease-drawer duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-drawer duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'relative max-w-xs w-full m-3 flex-1 flex flex-col pt-3 pb-4',
              sidebarGlassClasses
            )}
          >
            <div className="absolute top-0 right-0 -mr-12 pt-2 z-[60] pointer-events-auto">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleSidebar(false);
                }}
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue/30 bg-surface/80 border border-subtle text-neutral-600 hover:text-neutral-900 transition-colors"
              >
                <span className="sr-only">Close sidebar</span>
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="flex-shrink-0 py-2 pl-3 pr-1.5 flex items-center">
              <Link href="/templates">
                <ThemeLogo height="24px" />
              </Link>
            </div>
            <div className="mt-5 flex-1 h-0 overflow-y-auto flex flex-col">
              <nav className="flex-1 px-1.5 space-y-0.5">
                <MenuItems />
              </nav>
              <NavPlanUpgrade
                source="dashboard_sidebar"
                className="flex-shrink-0 px-4 pb-2 pt-4"
              />
            </div>
          </Transition.Child>
          <div className="flex-shrink-0 w-14" />
        </Transition>
      </div>

      {/* Floating glass sidebar — desktop */}
      <div className="sidebar sidebar-desktop hidden md:flex md:flex-shrink-0 self-stretch py-3 pl-3">
        <div
          className={cn(
            'w-[248px] flex flex-col flex-grow overflow-hidden',
            sidebarGlassClasses
          )}
        >
          <div className="pt-3 pb-4 flex flex-col flex-grow overflow-y-auto">
            <div className="flex-shrink-0 py-2 pl-3 pr-1.5 flex items-center">
              <Link href="/">
                <ThemeLogo height="24px" />
              </Link>
            </div>
            <div className="flex-grow mt-5 flex flex-col min-h-0">
              <nav className="flex-1 px-1.5 space-y-0.5 overflow-y-auto">
                <MenuItems />
              </nav>
              <NavPlanUpgrade
                source="dashboard_sidebar"
                className="flex-shrink-0 px-4 pb-2 pt-4"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const Dashboard = ({ children, options = {} }) => {
  const isMounted = React.useRef(true);
  const [isOpen, toggleSidebar] = useState(false);
  const toggleIfMounted = (val) => {
    if (isMounted.current) {
      return toggleSidebar(val);
    }
  };
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  const { user, loading } = useAuth();

  return (
    <div className="min-h-screen bg-postdrop-light overflow-hidden flex relative">
      <AccountAnalyticsSync />
      {/* Ambient atmosphere for glass refraction */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <div className="absolute -top-24 -left-16 h-[420px] w-[420px] rounded-full bg-blue/[0.04] blur-3xl dark:bg-blue/[0.07]" />
        <div className="absolute -bottom-32 -right-20 h-[480px] w-[480px] rounded-full bg-pink/[0.035] blur-3xl dark:bg-pink/[0.06]" />
      </div>

      <Sidebar isOpen={isOpen} toggleSidebar={toggleIfMounted} />

      <div className="relative flex-1 max-w-full mx-auto w-0 flex flex-col md:px-8 xl:px-8">
        <div className="relative z-10 flex-shrink-0 h-12 border-b md:border-none border-subtle flex">
          <button
            onClick={() => toggleSidebar(true)}
            className="border-r border-subtle px-4 text-neutral-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue/30 md:hidden"
          >
            <span className="sr-only">Open sidebar</span>
            <svg
              className="h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h7"
              />
            </svg>
          </button>
          <div className="flex-1 flex justify-between px-4 md:px-0">
            <div className="flex-1 flex">
              <div className="md:hidden flex-1 flex justify-center items-center">
                <Link href="/">
                  <ThemeLogo height="24px" />
                </Link>
              </div>
            </div>
            {!loading && user && (
              <div className="ml-4 flex items-center md:ml-6">
                <div className="ml-3 relative">
                  <Avatar />
                </div>
              </div>
            )}
          </div>
        </div>
        <main
          className="flex-1 relative overflow-y-auto overflow-x-hidden focus:outline-none"
          tabIndex={0}
        >
          <div className="pb-6 pt-6 md:pt-0 w-full max-w-6xl mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
